import { useState, useEffect } from "react";
import Select from "../ui/Select";
import {
  Save,
  Building2,
  CreditCard,
  ShieldAlert,
  Sliders,
  ShieldCheck,
  Truck,
  Sparkles,
  Briefcase,
  LifeBuoy,
  Bell,
  MessageSquare,
  CheckCircle2,
} from "lucide-react";
import {
  getSocietyConfigs,
  updateSocietyConfigs,
} from "../../services/societyService";
import {
  normalizePropertyType,
  PROPERTY_CATEGORIES,
} from "../../utils/propertyStructure";
import { getCurrentRoleKey, getStoredAuthUser } from "../../services/authService";
import PropertyBbpsSettingsSection from "./PropertyBbpsSettingsSection";

const DEFAULT_MODULES = {
  visitors: true,
  gateTerminal: true,
  parking: true,
  amenities: true,
  services: true,
  helpdesk: true,
  notices: true,
  chats: true,
};

const SwitchToggle = ({
  checked = false,
  onChange,
  activeColor = "var(--color-dark-bg, #27306f)",
  inactiveColor = "#cbd5e1",
  size = "md", // "sm" | "md"
  disabled = false,
}) => {
  const isSm = size === "sm";
  const width = isSm ? 32 : 36;
  const height = isSm ? 18 : 20;
  const knobSize = isSm ? 14 : 16;
  const knobOffset = width - knobSize - 2;

  return (
    <div
      role="switch"
      aria-checked={checked}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) onChange(!checked);
      }}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: `${height}px`,
        background: checked ? activeColor : inactiveColor,
        position: "relative",
        transition: "background 0.2s ease",
        flexShrink: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div
        style={{
          width: `${knobSize}px`,
          height: `${knobSize}px`,
          borderRadius: "50%",
          background: "#ffffff",
          position: "absolute",
          top: "2px",
          left: checked ? `${knobOffset}px` : "2px",
          transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        }}
      />
    </div>
  );
};

const PropertySettings = ({ societyId, propertyType }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;

  const currentRole = getCurrentRoleKey();
  const authUser = getStoredAuthUser();
  const designation = String(authUser?.role_details?.designation || authUser?.primary_role || "").toLowerCase();
  // Authorized if Super Admin, Director, or Board executive
  const canEditRent =
    currentRole === "super-admin" ||
    designation.includes("director") ||
    designation.includes("board") ||
    designation.includes("general manager");

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    registrationNumber: "",
    maintenanceAmount: "1500",
    maintenanceBillingCycle: "Monthly",
    penaltyRate: "2",
    enableLeasing: false,
    rentalDepositMonths: "3",
    rentAmount: "",
    rentDueDay: "5",
    rentSetOnce: false,
    modules: DEFAULT_MODULES,
    industrialRules: {
      mandatorySelfie: true,
      mandatoryDeskCabin: false,
      allowVehicleGate: true,
      autoCheckoutEod: true,
      floorReceptionistFallback: true,
      hodDelegation: true,
    },
    residentialRules: {
      petsAllowed: true,
      bachelorsAllowed: true,
      commercialUseAllowed: false,
      musicAfter10pm: false,
    },
  });

  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (societyId) fetchConfigs();
  }, [societyId]);

  const fetchConfigs = async () => {
    setLoading(true);
    const result = await getSocietyConfigs(societyId);
    if (result && Object.keys(result).length > 0) {
      const configObj = result.profile_config
        ? typeof result.profile_config === "string"
          ? JSON.parse(result.profile_config)
          : result.profile_config
        : {};

      setFormData((prev) => ({
        ...prev,
        name: result.name || result.society_name || prev.name,
        address: result.address || prev.address,
        registrationNumber: result.registration_number || prev.registrationNumber,
        maintenanceAmount:
          result.maintenance_amount || prev.maintenanceAmount,
        maintenanceBillingCycle:
          result.maintenance_billing_cycle || prev.maintenanceBillingCycle,
        penaltyRate: result.penalty_rate || prev.penaltyRate,
        enableLeasing: Boolean(configObj.enable_leasing ?? configObj.enableLeasing ?? false),
        rentalDepositMonths: String(configObj.rental_deposit_months ?? configObj.rentalDepositMonths ?? "3"),
        rentAmount: configObj.base_rent_amount ? String(configObj.base_rent_amount) : prev.rentAmount,
        rentDueDay: configObj.rent_due_day ? String(configObj.rent_due_day) : "5",
        rentSetOnce: Boolean(configObj.base_rent_amount && configObj.rent_due_day),
        modules: {
          ...DEFAULT_MODULES,
          ...(configObj.modules || {}),
        },
        industrialRules: {
          mandatorySelfie: configObj.requiresLiveSelfie ?? true,
          mandatoryDeskCabin: configObj.mandatoryDeskCode ?? false,
          allowVehicleGate: configObj.allow_vehicle_at_gate ?? true,
          autoCheckoutEod: configObj.auto_checkout_eod ?? true,
          floorReceptionistFallback:
            configObj.supportsFloorReceptionist ?? true,
          hodDelegation:
            configObj.supportsDepartmentHeadFallback ?? true,
        },
        residentialRules: result.global_rules
          ? typeof result.global_rules === "string"
            ? JSON.parse(result.global_rules)
            : result.global_rules
          : prev.residentialRules,
      }));
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleModuleToggle = (moduleKey) => {
    setFormData((prev) => ({
      ...prev,
      modules: {
        ...prev.modules,
        [moduleKey]: !prev.modules[moduleKey],
      },
    }));
  };

  const handleIndustrialRuleToggle = (ruleKey) => {
    setFormData((prev) => ({
      ...prev,
      industrialRules: {
        ...prev.industrialRules,
        [ruleKey]: !prev.industrialRules[ruleKey],
      },
    }));
  };

  const handleResidentialRuleToggle = (ruleKey) => {
    setFormData((prev) => ({
      ...prev,
      residentialRules: {
        ...prev.residentialRules,
        [ruleKey]: !prev.residentialRules[ruleKey],
      },
    }));
  };

  const handleSave = async () => {
    const payload = {
      society_name: formData.name,
      address: formData.address,
      maintenance_amount: formData.maintenanceAmount,
      maintenance_billing_cycle: formData.maintenanceBillingCycle,
      penalty_rate: formData.penaltyRate,
      global_rules: formData.residentialRules,
      profile_config: {
        modules: formData.modules,
        enable_leasing: formData.enableLeasing,
        rental_deposit_months: formData.rentalDepositMonths,
        base_rent_amount: formData.rentAmount ? Number(formData.rentAmount) : null,
        rent_due_day: formData.rentDueDay ? Number(formData.rentDueDay) : null,
        requiresLiveSelfie: formData.industrialRules.mandatorySelfie,
        mandatoryCabinCode: formData.industrialRules.mandatoryDeskCabin,
        mandatoryDeskCode: formData.industrialRules.mandatoryDeskCabin,
        allow_vehicle_at_gate: formData.industrialRules.allowVehicleGate,
        auto_checkout_eod: formData.industrialRules.autoCheckoutEod,
        supportsFloorReceptionist:
          formData.industrialRules.floorReceptionistFallback,
        supportsDepartmentHeadFallback: formData.industrialRules.hodDelegation,
      },
    };

    const res = await updateSocietyConfigs(societyId, payload);
    if (res || res === undefined) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const sectionStyle = {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#0f172a",
    fontSize: "16px",
    fontWeight: "700",
    marginBottom: "18px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f1f5f9",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    color: "#475569",
    fontSize: "13.5px",
    fontWeight: "600",
  };

  const moduleItems = [
    {
      key: "visitors",
      title: "Visitor Management",
      desc: isIndustrial
        ? "Walk-in employee host approvals, pass issuance & OTP verification"
        : "Pre-approved passes, walk-in notifications & guest logs",
      icon: ShieldCheck,
      color: "var(--color-dark-bg)",
    },
    {
      key: "gateTerminal",
      title: "Gate Security Console",
      desc: "Live gate terminal, pass QR scanner & countdown drafts",
      icon: ShieldAlert,
      color: "#dc2626",
    },
    {
      key: "parking",
      title: isIndustrial ? "Parking & Loading Bays" : "Parking Management",
      desc: "Vehicle pass tracking, slot allocation & cargo entry",
      icon: Truck,
      color: "#059669",
    },
    {
      key: "amenities",
      title: isIndustrial ? "Facility & Room Booking" : "Amenities Catalog",
      desc: isIndustrial
        ? "Conference rooms, auditorium & training halls"
        : "Clubhouse, gym, pool & sport courts",
      icon: Sparkles,
      color: "#d97706",
    },
    {
      key: "services",
      title: "On-Demand Services",
      desc: "Housekeeping, electrician, maintenance & vendor requests",
      icon: Briefcase,
      color: "#4f46e5",
    },
    {
      key: "helpdesk",
      title: "Helpdesk & Tickets",
      desc: "Issue tracking, maintenance requests & resolution SLAs",
      icon: LifeBuoy,
      color: "#0891b2",
    },
    {
      key: "notices",
      title: "Notice Broadcasts",
      desc: "Emergency push alerts, administrative circulars & SOP announcements",
      icon: Bell,
      color: "#7c3aed",
    },
    {
      key: "chats",
      title: isIndustrial ? "Department Groups" : "Community Chats",
      desc: "Moderated communication channels and team discussions",
      icon: MessageSquare,
      color: "#e11d48",
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
        Loading property configuration...
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 4px 32px 4px", maxWidth: "1080px" }}>
      {/* Top Action Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h3
            style={{
              color: "#0f172a",
              marginBottom: "4px",
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            Settings & Operational Modules
          </h3>
          <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
            Configure active operational modules, security rules, and property details for this property.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {saveSuccess && (
            <span
              style={{
                color: "#16a34a",
                fontSize: "13.5px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <CheckCircle2 size={16} /> Saved!
            </span>
          )}
          <button
            onClick={handleSave}
            style={{
              padding: "10px 20px",
              background: "var(--color-dark-bg)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: "600",
              fontSize: "14px",
              boxShadow: "0 2px 4px rgba(37,99,235,0.2)",
            }}
          >
            <Save size={16} /> Save Changes
          </button>
        </div>
      </div>

      {/* Section 1: Operational Module Toggles */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <Sliders size={18} color="var(--color-dark-bg)" />
          <span>Operational Module Toggles</span>
          <span
            style={{
              marginLeft: "auto",
              fontSize: "12px",
              fontWeight: "700",
              padding: "2px 8px",
              borderRadius: "12px",
              background: "#eff6ff",
              color: "var(--color-dark-bg)",
              textTransform: "uppercase",
            }}
          >
            {normalizedCategory} Profile
          </span>
        </div>
        <p
          style={{
            color: "#64748b",
            fontSize: "13px",
            marginTop: "-8px",
            marginBottom: "18px",
          }}
        >
          Toggle operational modules for this specific property. Enabled modules dictate what features, screens, and workflows are available.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
            gap: "16px",
          }}
        >
          {moduleItems.map((mod) => {
            const Icon = mod.icon;
            const isEnabled = Boolean(formData.modules[mod.key]);

            return (
              <div
                key={mod.key}
                onClick={() => handleModuleToggle(mod.key)}
                style={{
                  border: isEnabled ? "1.5px solid var(--color-dark-bg)" : "1px solid #e2e8f0",
                  background: isEnabled ? "rgba(37, 99, 235, 0.02)" : "#fff",
                  borderRadius: "10px",
                  padding: "16px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "14px",
                  transition: "all 150ms ease",
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "8px",
                    background: isEnabled ? `${mod.color}15` : "#f1f5f9",
                    color: isEnabled ? mod.color : "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: "14px",
                        color: isEnabled ? "#0f172a" : "#64748b",
                      }}
                    >
                      {mod.title}
                    </span>
                    <SwitchToggle
                      checked={isEnabled}
                      onChange={() => handleModuleToggle(mod.key)}
                      size="sm"
                      activeColor="var(--color-dark-bg, #27306f)"
                    />
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "#64748b",
                      lineHeight: "1.4",
                    }}
                  >
                    {mod.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 2: Property Type-Specific Security Rules */}
      {isIndustrial ? (
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <ShieldAlert size={18} color="#dc2626" />
            <span>Industrial Security & Workflow Rules</span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "18px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ flex: 1, paddingRight: "16px" }}>
                <div style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                  Mandatory Live Selfie for Walk-ins
                </div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px", lineHeight: "1.4" }}>
                  Enforces selfie capture at gate terminal or public kiosk.
                </div>
              </div>
              <div style={{ marginTop: "2px" }}>
                <SwitchToggle
                  checked={formData.industrialRules.mandatorySelfie}
                  onChange={() => handleIndustrialRuleToggle("mandatorySelfie")}
                  activeColor="var(--color-dark-bg, #27306f)"
                />
              </div>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ flex: 1, paddingRight: "16px" }}>
                <div style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                  Require Fixed Desks & Private Cabins
                </div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px", lineHeight: "1.4" }}>
                  When disabled (recommended for open layouts & flexible offices), desks are automatically counted based on floor staff, and cabins are optional for HODs/directors.
                </div>
              </div>
              <div style={{ marginTop: "2px" }}>
                <SwitchToggle
                  checked={formData.industrialRules.mandatoryDeskCabin}
                  onChange={() => handleIndustrialRuleToggle("mandatoryDeskCabin")}
                  activeColor="var(--color-dark-bg, #27306f)"
                />
              </div>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ flex: 1, paddingRight: "16px" }}>
                <div style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                  Auto-Checkout Passes at EOD (11:59 PM)
                </div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px", lineHeight: "1.4" }}>
                  Automatically closes un-exited passes at midnight with audit status.
                </div>
              </div>
              <div style={{ marginTop: "2px" }}>
                <SwitchToggle
                  checked={formData.industrialRules.autoCheckoutEod}
                  onChange={() => handleIndustrialRuleToggle("autoCheckoutEod")}
                  activeColor="var(--color-dark-bg, #27306f)"
                />
              </div>
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ flex: 1, paddingRight: "16px" }}>
                <div style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                  Floor Receptionist Fallback Dispatch
                </div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px", lineHeight: "1.4" }}>
                  Routes unattended requests to host&apos;s floor receptionist after 180s.
                </div>
              </div>
              <div style={{ marginTop: "2px" }}>
                <SwitchToggle
                  checked={formData.industrialRules.floorReceptionistFallback}
                  onChange={() => handleIndustrialRuleToggle("floorReceptionistFallback")}
                  activeColor="var(--color-dark-bg, #27306f)"
                />
              </div>
            </label>
          </div>
        </div>
      ) : (
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <ShieldAlert size={18} color="#dc2626" />
            <span>Community & Premises Rules</span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "18px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                Allow Pets in Premises
              </span>
              <SwitchToggle
                checked={formData.residentialRules.petsAllowed}
                onChange={() => handleResidentialRuleToggle("petsAllowed")}
                activeColor="var(--color-dark-bg, #27306f)"
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                Allow Bachelor Tenants
              </span>
              <SwitchToggle
                checked={formData.residentialRules.bachelorsAllowed}
                onChange={() => handleResidentialRuleToggle("bachelorsAllowed")}
                activeColor="var(--color-dark-bg, #27306f)"
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                Allow Commercial Use of Flats
              </span>
              <SwitchToggle
                checked={formData.residentialRules.commercialUseAllowed}
                onChange={() => handleResidentialRuleToggle("commercialUseAllowed")}
                activeColor="var(--color-dark-bg, #27306f)"
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                cursor: "pointer",
                background: "#f8fafc",
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13.5px", color: "#0f172a" }}>
                Allow Loud Music After 10 PM
              </span>
              <SwitchToggle
                checked={formData.residentialRules.musicAfter10pm}
                onChange={() => handleResidentialRuleToggle("musicAfter10pm")}
                activeColor="var(--color-dark-bg, #27306f)"
              />
            </label>
          </div>
        </div>
      )}

      {/* Section 3: Basic Info */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <Building2 size={18} color="#4f46e5" />
          <span>Basic Information</span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
          }}
        >
          <div>
            <label style={labelStyle}>Property Name</label>
            <input
              type="text"
              style={inputStyle}
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Registration Number</label>
            <input
              type="text"
              style={inputStyle}
              value={formData.registrationNumber}
              onChange={(e) =>
                handleInputChange("registrationNumber", e.target.value)
              }
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Full Address</label>
            <textarea
              style={{ ...inputStyle, height: "76px", resize: "vertical" }}
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Section 4: Financials */}
      {/* BBPS Utility & BillPay Category Settings */}
      <PropertyBbpsSettingsSection societyId={societyId} isIndustrial={isIndustrial} />

      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <CreditCard size={18} color="#059669" />
          <span>Financials & Maintenance</span>
        </div>
        {/* Property Leasing / Renting Toggle */}
        <div style={{ marginBottom: "20px" }}>
          <div
            onClick={() => handleInputChange("enableLeasing", !formData.enableLeasing)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 16px",
              border: formData.enableLeasing
                ? "1.5px solid var(--color-dark-bg, #059669)"
                : "1px solid #e2e8f0",
              borderRadius: "10px",
              cursor: "pointer",
              background: formData.enableLeasing
                ? "rgba(5, 150, 105, 0.03)"
                : "#f8fafc",
              transition: "all 150ms ease",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "14px",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>Enable Property Renting & Unit Leasing</span>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: formData.enableLeasing ? "#dcfce7" : "#e2e8f0",
                    color: formData.enableLeasing ? "#15803d" : "#64748b",
                  }}
                >
                  {formData.enableLeasing ? "Active" : "Disabled"}
                </span>
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginTop: "3px",
                  lineHeight: "1.4",
                }}
              >
                {isIndustrial
                  ? "Allows external tenants or corporate sub-lessees to lease workplace bays, cabins, or warehouse wings."
                  : "Allows apartment owners to onboard verified tenants, register lease agreements, and manage tenant moves."}
              </div>
            </div>

            <SwitchToggle
              checked={formData.enableLeasing}
              onChange={(nextVal) => handleInputChange("enableLeasing", nextVal)}
              activeColor="#059669"
            />
          </div>
        </div>

        {/* Financial Details: Rent, Due Date & Maintenance only active when Leased */}
        {formData.enableLeasing ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "18px",
              paddingTop: "14px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            {/* Rent & Due Date Configuration */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "700", fontSize: "13.5px", color: "#0f172a" }}>
                  Lease Rent & Monthly Due Date
                </span>
                {formData.rentSetOnce && !canEditRent && (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      background: "#fef3c7",
                      color: "#b45309",
                    }}
                  >
                    🔒 Locked (Director / Super Admin Only)
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  padding: "16px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <label style={labelStyle}>Base Monthly Rent (₹)</label>
                  <input
                    type="number"
                    style={{
                      ...inputStyle,
                      background: formData.rentSetOnce && !canEditRent ? "#e2e8f0" : "#fff",
                      cursor: formData.rentSetOnce && !canEditRent ? "not-allowed" : "text",
                    }}
                    value={formData.rentAmount}
                    disabled={formData.rentSetOnce && !canEditRent}
                    onChange={(e) => handleInputChange("rentAmount", e.target.value)}
                    placeholder="e.g. 25000"
                  />
                  <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "block" }}>
                    {formData.rentSetOnce
                      ? (canEditRent ? "You have executive authority to modify rent amount." : "Rent rate is fixed and locked once set.")
                      : "Can be set once. Future modifications require highest power score."}
                  </span>
                </div>
                <div>
                  <label style={labelStyle}>Rent Due Day of Month</label>
                  <Select
                    value={formData.rentDueDay}
                    disabled={formData.rentSetOnce && !canEditRent}
                    onChange={(e) => handleInputChange("rentDueDay", e.target.value)}
                    options={[
                      { value: "1", label: "1st of every month" },
                      { value: "5", label: "5th of every month" },
                      { value: "10", label: "10th of every month" },
                      { value: "15", label: "15th of every month" },
                      { value: "25", label: "25th of every month" },
                    ]}
                  />
                  <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "block" }}>
                    Automated rent invoice generation and payment reminder schedule.
                  </span>
                </div>
              </div>
            </div>

            {/* Maintenance & Late Fee Sub-section (Active only when Leased) */}
            <div>
              <div style={{ fontWeight: "700", fontSize: "13.5px", color: "#0f172a", marginBottom: "8px" }}>
                Maintenance & Billing Parameters
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "16px",
                  padding: "16px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <label style={labelStyle}>Base Maintenance (₹)</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={formData.maintenanceAmount}
                    onChange={(e) =>
                      handleInputChange("maintenanceAmount", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label style={labelStyle}>Billing Cycle</label>
                  <Select
                    value={formData.maintenanceBillingCycle}
                    onChange={(e) =>
                      handleInputChange("maintenanceBillingCycle", e.target.value)
                    }
                    options={[
                      { value: "Monthly", label: "Monthly" },
                      { value: "Quarterly", label: "Quarterly" },
                      { value: "Half-Yearly", label: "Half-Yearly" },
                      { value: "Annually", label: "Annually" },
                    ]}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Late Penalty Rate (%)</label>
                  <input
                    type="number"
                    style={inputStyle}
                    value={formData.penaltyRate}
                    onChange={(e) => handleInputChange("penaltyRate", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Lease Terms */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                padding: "14px",
                background: "#f1f5f9",
                borderRadius: "8px",
              }}
            >
              <div>
                <label style={labelStyle}>Standard Security Deposit (Months)</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={formData.rentalDepositMonths}
                  onChange={(e) =>
                    handleInputChange("rentalDepositMonths", e.target.value)
                  }
                  placeholder="e.g. 3"
                />
              </div>
              <div>
                <label style={labelStyle}>Tenant Move-In SLA (Days)</label>
                <input
                  type="number"
                  style={inputStyle}
                  defaultValue="7"
                  placeholder="e.g. 7"
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: "24px 16px",
              textAlign: "center",
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              borderRadius: "8px",
              color: "#64748b",
              fontSize: "13px",
            }}
          >
            🔒 <strong>Maintenance and Rent billing parameters are currently disabled.</strong>
            <div style={{ marginTop: "4px", fontSize: "12px", color: "#94a3b8" }}>
              Enable <em>&quot;Property Renting &amp; Unit Leasing&quot;</em> above to configure lease rent, payment due date, and maintenance fee schedules.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertySettings;
